import { useState } from "react";
import { motion } from "framer-motion";
import content from "../content";
import { SectionBg } from "./SectionBg";
import VerseShare from "./VerseShare";
import { highlightMask } from "../lib/verseImage";

const wordContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.055, delayChildren: 0.1 } },
};
const wordItem = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const eyebrowFade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function VerseSection() {
  const { verse } = content;
  const [active, setActive] = useState(0);
  const version = verse.versions[active];
  const words = version.text.split(" ");
  const mask = highlightMask(version.text, version.highlight);

  return (
    <section
      id="verso"
      className="relative w-full bg-ink overflow-hidden py-28 md:py-40"
      data-testid="verse-section"
    >
      <SectionBg image="/images/hero-worship.webp" tint="ink" />
      {/* Faint floating particles */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {[
          "top-[18%] left-[12%]",
          "top-[30%] right-[15%]",
          "bottom-[22%] left-[22%]",
          "bottom-[30%] right-[24%]",
          "top-[55%] left-[46%]",
        ].map((pos, i) => (
          <span
            key={pos}
            className={`absolute ${pos} w-2 h-2 rounded-full bg-electric/70 blur-[2px] animate-float`}
            style={{ animationDelay: `${i * 0.7}s`, animationDuration: `${6 + i}s` }}
          />
        ))}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vw] max-w-[720px] max-h-[720px] rounded-full opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(33,112,232,0.22), transparent 62%)",
          }}
        />
      </div>

      <div className="relative w-full max-w-5xl mx-auto px-6 text-center">
        <motion.p {...eyebrowFade} className="eyebrow mb-8">
          {verse.eyebrow}
        </motion.p>

        {/* Toggle de versión: RVR / ESV */}
        <div
          className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1 mb-10"
          data-testid="verse-toggle"
          role="group"
          aria-label="Versión de la Biblia"
        >
          {verse.versions.map((v, i) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setActive(i)}
              aria-pressed={active === i}
              data-testid={`verse-toggle-${v.id.toLowerCase()}`}
              className={`rounded-full px-5 py-1.5 font-poppins text-xs font-semibold tracking-[0.18em] uppercase transition-colors duration-300 cursor-pointer ${
                active === i ? "bg-electric text-white shadow-[0_0_20px_rgba(33,112,232,0.5)]" : "text-white/55 hover:text-white"
              }`}
            >
              {v.id}
            </button>
          ))}
        </div>

        <motion.blockquote
          key={version.id}
          variants={wordContainer}
          initial="hidden"
          animate="show"
          className="display text-white text-[clamp(1.9rem,5.5vw,4.75rem)] leading-[1.02] max-w-4xl mx-auto"
          data-testid="verse-text"
        >
          {words.map((w, i) => {
            const isHighlight = mask[i];
            return (
              <motion.span
                key={`${w}-${i}`}
                variants={wordItem}
                className={`inline-block mr-[0.28em] ${isHighlight ? "text-electric text-glow-blue" : ""}`}
              >
                {w}
              </motion.span>
            );
          })}
        </motion.blockquote>

        <motion.cite
          key={`${version.id}-ref`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="not-italic block mt-10 font-poppins text-sm md:text-base tracking-[0.22em] uppercase text-electric"
          data-testid="verse-reference"
        >
          {version.reference}
        </motion.cite>

        <VerseShare />
      </div>
    </section>
  );
}
